import { defineStore } from 'pinia'
import type { Competency, MentorshipRule, MentorshipSettings, ProgramTemplate } from '../types'
import { mentorshipApi } from '../services/mentorshipApi'
import { getApiError } from '@/composables/getApiError'

const defaultSettings = (): Omit<MentorshipSettings, 'id'> => ({
  max_active_mentees_per_mentor: 3,
  activity_reminder_days: 3,
  inactivity_risk_days: 14,
  require_final_documents: true,
})

export const useMentorshipSettingsStore = defineStore('mentorshipSettings', {
  state: () => ({
    settings: defaultSettings() as MentorshipSettings | Omit<MentorshipSettings, 'id'>,
    rules: [] as MentorshipRule[],
    templates: [] as ProgramTemplate[],
    competencies: [] as Competency[],
    loading: false,
    saving: false,
    error: null as string | null,
  }),
  actions: {
    async fetchCatalogs() {
      this.loading = true
      this.error = null
      try {
        [this.templates, this.competencies] = await Promise.all([mentorshipApi.getProgramTemplates(), mentorshipApi.getCompetencies()])
      } catch (error) {
        this.error = getApiError(error, 'Не удалось загрузить программы и компетенции')
      } finally {
        this.loading = false
      }
    },
    async fetchAll() {
      this.loading = true
      this.error = null
      try {
        const [settings, rules, templates, competencies] = await Promise.all([
          mentorshipApi.getSettings(), mentorshipApi.getRules(), mentorshipApi.getProgramTemplates(), mentorshipApi.getCompetencies(),
        ])
        this.settings = settings ?? defaultSettings()
        this.rules = rules
        this.templates = templates
        this.competencies = competencies
      } catch (error) {
        this.error = getApiError(error, 'Не удалось загрузить настройки наставничества')
      } finally {
        this.loading = false
      }
    },
    async saveSettings() {
      this.saving = true
      this.error = null
      try {
        const { id, ...payload } = this.settings as MentorshipSettings
        this.settings = await mentorshipApi.saveSettings(payload, id)
      } catch (error) {
        this.error = getApiError(error, 'Не удалось сохранить настройки наставничества')
        throw error
      } finally {
        this.saving = false
      }
    },
  },
})
